import { ServiceLines } from '../enums/index';
import { IdentifierFormat, detectIdFormat } from './identifier-utils';

/**
 * Enum representing the different generations of the Automate feature.
 * - `GEN1`: The first generation of the Automate feature.  Originally Rad and Path.
 * - `GEN2`: The second generation of the Automate feature. Originally BSP.
 * - `GEN3`: The third generation of the Automate feature. Originally Radv2, will be all service lines.
 */
export enum AutomateGeneration {
  GEN1 = 'GEN1',
  GEN2 = 'GEN2',
  GEN3 = 'GEN3'
}

const v1 = [`${ServiceLines.RADIOLOGY}`, `${ServiceLines.PATHOLOGY}`];
const V1_ALLOWED = new Set(v1.map(l => l.toUpperCase()));

const v2 = [`${ServiceLines.SURGERY}`];
const V2_ALLOWED = new Set(v2.map(l => l.toUpperCase()));

const v3 = [`${ServiceLines.IPC}`, `${ServiceLines.IPC_LONG_NAME}`];
const V3_ALLOWED = new Set(v3.map(l => l.toUpperCase()));

// --------------------------------
//  Service lines Definitions
// --------------------------------

const baseParameters = [
  ['evidence', 'true'],
  ['processTypes', 'PREDICTION'],
  ['processTypes', 'ADDENDUM']
];

const gen3Parameters = [
  ['evidence', 'true'],
  ['processTypes', 'PREDICTION'],
  ['processTypes', 'ADDENDUM'],
  ['view', 'amplify']
];

const DEFAULT_SL: CMxCommonApp.ServiceLineConfig = {
  path: '',
  amplifyPath: '',
  caseIdProp: 'case_id',
  processInstanceIdprop: 'process_instance_id',
  caseDetailVersion: 'v2',
  caseListVersion: 'v2',
  automateGen: AutomateGeneration.GEN1,
  caseDetailParms: [...baseParameters]
};

const RADIOLOGY_V1_SL: CMxCommonApp.ServiceLineConfig = {
  path: '',
  caseIdProp: 'case_id',
  processInstanceIdprop: 'process_instance_id',
  amplifyPath: 'radiology',
  caseListVersion: 'v2',
  caseDetailVersion: 'v4',
  automateGen: AutomateGeneration.GEN1,
  caseDetailParms: [...baseParameters]
};

const RADIOLOGY_V2_SL: CMxCommonApp.ServiceLineConfig = {
  path: '/api/cases/RADIOLOGY',
  amplifyPath: 'radiology',
  caseIdProp: 'case_uid',
  processInstanceIdprop: 'caserun_uid',
  caseListVersion: 'v2',
  caseDetailVersion: 'v1',
  automateGen: AutomateGeneration.GEN3,
  caseDetailParms: [...gen3Parameters],
  ignoreEvidence: true
};

const PATHOLOGY_V1_SL: CMxCommonApp.ServiceLineConfig = {
  path: '/api/cases/PATHOLOGY',
  amplifyPath: 'pathology',
  caseIdProp: 'case_id',
  processInstanceIdprop: 'process_instance_id',
  processInstanceGuidProp: 'process_instance_guid',
  caseListVersion: 'v2',
  caseDetailVersion: 'v2',
  automateGen: AutomateGeneration.GEN1,
  caseDetailParms: [...baseParameters]
};

const PATHOLOGY_V2_SL: CMxCommonApp.ServiceLineConfig = {
  path: '/api/cases/PATHOLOGY',
  amplifyPath: 'pathology',
  caseIdProp: 'case_uid',
  processInstanceIdprop: 'caserun_uid',
  caseListVersion: 'v2',
  caseDetailVersion: 'v1',
  automateGen: AutomateGeneration.GEN3,
  caseDetailParms: [...gen3Parameters]
};

const SURGERY_SL: CMxCommonApp.ServiceLineConfig = {
  path: '/api/cases/SURGERY',
  amplifyPath: 'surgery',
  caseIdProp: 'case_uid',
  processInstanceIdprop: 'process_instance_guid',
  caseListVersion: 'v2',
  caseDetailVersion: 'v1',
  automateGen: AutomateGeneration.GEN3,
  caseDetailParms: [...baseParameters]
};

const AUTOMATE_SL: CMxCommonApp.ServiceLineConfig = {
  path: '/api/cases/BEDSIDEPRO',
  caselistAltId: 'BEDSIDEPRO',
  amplifyPath: 'ipc',
  caseIdProp: 'case_uid',
  processInstanceIdprop: 'process_instance_guid',
  caseListVersion: 'v2',
  caseDetailVersion: 'v1',
  automateGen: AutomateGeneration.GEN2,
  caseDetailParms: [...baseParameters]
};

// --------------------------------
//  URL helpers
// --------------------------------

/**
 * Convert a service-line name into a URL-safe slug.
 * "Bedside Pro" → "bedside-pro", "Radiology" → "radiology".
 */
export const toServiceLineSlug = (name: string): string =>
  name.trim().toLowerCase().replaceAll(/\s+/g, '-');

/**
 * Normalize a value that may have come from a URL segment back into the
 * canonical lowercase-with-spaces form so it can be compared against enum
 * values (which `determineServiceLineConfig` already uppercases internally).
 *
 * Handles: URI-encoded spaces (`%20`), hyphens, and mixed casing.
 * "bedside-pro" | "bedside%20pro" | "Bedside Pro" → "bedside pro"
 */
export const normalizeServiceLineName = (raw: string): string =>
  decodeURIComponent(raw).trim().toLowerCase().replaceAll('-', ' ');

// --------------------------------
//  API
// --------------------------------

/**
 * Determines whether the provided service line name represents the IPC/BSP service line.
 * This was required because there are numerous service line names for BSP.
 *
 * @param serviceLine - The service line name to check.
 * @returns `true` if the service line represents the IPC service line, `false` otherwise.
 */
export const isIPC = (serviceLine: string) => {
  if (!serviceLine || serviceLine.length === 0) {
    return false;
  }

  let ipcNames = new Set([
    ServiceLines.IPC.toUpperCase(),
    ServiceLines.IPC_LONG_NAME.toUpperCase(),
    ServiceLines.AUTOMATE.toUpperCase(),
    ServiceLines.BEDSIDE_PRO.toUpperCase()
  ]);

  return ipcNames.has(normalizeServiceLineName(serviceLine).toUpperCase());
};

export const isSurgery = (serviceLine: string) => {
  if (!serviceLine || serviceLine.length === 0) {
    return false;
  }

  let surgeryNames = new Set([ServiceLines.SURGERY.toUpperCase()]);

  return surgeryNames.has(normalizeServiceLineName(serviceLine).toUpperCase());
};

export const isPathology = (serviceLine: string) => {
  if (!serviceLine || serviceLine.length === 0) {
    return false;
  }

  let pathologyNames = new Set([ServiceLines.PATHOLOGY.toUpperCase()]);

  return pathologyNames.has(normalizeServiceLineName(serviceLine).toUpperCase());
};

/**
 * Represents a partial service line object with an optional case identifier.
 *
 * @property {string} [caseIdentifier] - The case identifier, if provided.  This can be eithere a case_id or a case_uid.
 * At runtime, we may not know programmatically which one to use, so we allow both.
 * @property {boolean} [displayAllCaseRuns] - Flag indicating whether to use v3 endpoint for pathology when enabled.
 */
type ServiceLineQuery = Partial<CMxAPI.ServiceLine> & {
  caseIdentifier?: string;
  displayAllCaseRuns?: boolean;
};

/**
 * Determines the appropriate Automate generation based on the provided case identifier and service line.
 *
 * @param caseIdentifier - The case identifier, which can be either a case_id or a case_uid.
 * @param serviceLine - The service line name.
 * @returns The appropriate Automate generation.
 */
const determineAutomateGeneration = (
  caseIdentifier: string,
  serviceLine: string
) => {
  if (V1_ALLOWED.has(serviceLine)) {
    const idFormat = detectIdFormat(caseIdentifier);
    if (idFormat === IdentifierFormat.GUID) {
      return AutomateGeneration.GEN3;
    } else {
      return AutomateGeneration.GEN1;
    }
  } else if (V2_ALLOWED.has(serviceLine)) {
    return AutomateGeneration.GEN2;
  } else if (V3_ALLOWED.has(serviceLine)) {
    return AutomateGeneration.GEN3;
  }
};

/**
 * Determines the appropriate service line configuration based on the provided service line query.
 *
 * @param sl - An optional partial service line object containing information to determine the appropriate configuration.
 * @returns The service line configuration object that matches the provided service line query.
 */
export const determineServiceLineConfig = (sl?: ServiceLineQuery) => {
  const name = normalizeServiceLineName(sl?.name ?? '').toUpperCase();
  let automateGen: AutomateGeneration;

  switch (name) {
    case `${ServiceLines.RADIOLOGY}`.toUpperCase():
      automateGen = sl?.caseIdentifier
        ? determineAutomateGeneration(sl.caseIdentifier, name)
        : AutomateGeneration.GEN1;
      return automateGen === AutomateGeneration.GEN3
        ? { ...RADIOLOGY_V2_SL }
        : { ...RADIOLOGY_V1_SL };
    case `${ServiceLines.PATHOLOGY}`.toUpperCase():
      automateGen = sl?.caseIdentifier
        ? determineAutomateGeneration(sl.caseIdentifier, name)
        : AutomateGeneration.GEN1;
      const pathologyConfig = automateGen === AutomateGeneration.GEN3
        ? { ...PATHOLOGY_V2_SL }
        : { ...PATHOLOGY_V1_SL };
      
      // Override caseDetailVersion to v3 for pathology when displayAllCaseRuns feature flag is enabled
      if (sl?.displayAllCaseRuns) {
        pathologyConfig.caseDetailVersion = 'v3';
      }
      
      return pathologyConfig;
    case `${ServiceLines.SURGERY}`.toUpperCase():
      return { ...SURGERY_SL };
    case `${ServiceLines.AUTOMATE}`.toUpperCase():
    case `${ServiceLines.BEDSIDE_PRO}`.toUpperCase():
    case `${ServiceLines.BEDSIDE_PRO_NO_SPACE}`.toUpperCase():
    case `${ServiceLines.IPC_LONG_NAME}`.toUpperCase():
    case ServiceLines.IPC:
      return { ...AUTOMATE_SL };
    default:
      return { ...DEFAULT_SL };
  }
};

/**
 * Determines the appropriate process instance ID property based on the provided service line query.
 *
 * @param sl - An optional partial service line object containing information to determine the appropriate process instance ID property.
 * @returns The process instance ID property that matches the provided service line query.
 */
export const determineProcessInstanceId = (
  sl?: Partial<CMxAPI.ServiceLine>
) => {
  const name = normalizeServiceLineName(sl?.name ?? '').toUpperCase();
  switch (name) {
    case `${ServiceLines.RADIOLOGY}`.toUpperCase():
      return RADIOLOGY_V1_SL.processInstanceIdprop;
    case `${ServiceLines.PATHOLOGY}`.toUpperCase():
      return PATHOLOGY_V1_SL.processInstanceGuidProp;
    case `${ServiceLines.SURGERY}`.toUpperCase():
      return SURGERY_SL.processInstanceIdprop;
    case `${ServiceLines.AUTOMATE}`.toUpperCase():
    case `${ServiceLines.BEDSIDE_PRO}`.toUpperCase():
    case `${ServiceLines.IPC_LONG_NAME}`.toUpperCase():
    case ServiceLines.IPC:
      return AUTOMATE_SL.processInstanceIdprop;
    default:
      return DEFAULT_SL.processInstanceIdprop;
  }
};

/**
 * Constructs the appropriate case detail URL based on service line configuration.
 * 
 * @param caseId - The case ID
 * @param serviceLineConfig - The service line configuration object
 * @returns The constructed URL for case details
 */
export const buildCaseDetailUrl = (
  caseId: string,
  serviceLineConfig: CMxCommonApp.ServiceLineConfig
): string => {
  if (serviceLineConfig.caseDetailVersion === 'v3') {
    return `${serviceLineConfig.path}/caseinstance/${caseId}/details/v3?evidence=true`;
  } else {
    return `${serviceLineConfig.path}/caseinstance/${caseId}/details/${serviceLineConfig.caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  }
};

let _context = DEFAULT_SL;

export const set = (sl: CMxCommonApp.ServiceLineConfig) => (_context = sl);
export const get = () => _context;
export const reset = () => (_context = DEFAULT_SL);
